import '@fontsource/barlow-condensed/latin-500.css';
import '@fontsource/barlow-condensed/latin-600.css';
import '@fontsource/barlow-condensed/latin-700.css';
import '@fontsource/barlow-condensed/latin-800.css';
import '@fontsource/barlow-condensed/latin-800-italic.css';
import '@fontsource/ibm-plex-mono/latin-400.css';
import '@fontsource/ibm-plex-mono/latin-500.css';
import '@fontsource/ibm-plex-mono/latin-600.css';
import './styles/main.css';
import './styles/compact-hud.css';
import './styles/hud-typography.css';
import gameRuntimeURL from 'virtual:scrapline-game-runtime-url';
import { CachedModuleImport } from './loader/CachedModuleImport';
import { ModuleRecoveryGuard } from './loader/ModuleRecoveryGuard';
import { createRuntimeConfig } from './config';
import type { Game, GameFrameState, GameResult, GraphicsPreference, PickupAwarenessNotice } from './game/core/Game';
import { InitialSceneTimeout } from './game/core/InitialSceneReadiness';
import { probeRendererCapabilities } from './game/core/RendererCapabilities';
import { gadgetDefinitions } from './game/content/gadgets';
import { weaponDefinitions } from './game/content/weapons';
import { seedFromText } from './game/content/regions';
import { maximumTrainCount, trainStageForNumber } from './game/content/trainStages';
import type { CouplerStatus, Credentials, ExtractionStatus, GadgetFailureNotice, HazardStatus, Lobby, MatchEvent, MatchResult, NetworkItemState, NetworkPlayer, NetworkWeaponState, Snapshot, SupplyStatus } from './network/protocol';
import type { ConnectionState, RealtimeClient } from './network/RealtimeClient';
import { IdentityService } from './network/IdentityService';
import { AccountFlow } from './ui/AccountFlow';
import { SessionRecovery } from './ui/SessionRecovery';
import { renderAppShell } from './ui/AppShell';
import { PasswordRotation } from './ui/PasswordRotation';
import { CombatFeedback } from './ui/CombatFeedback';
import { extractionObjective, nextMultiplier, projectExtractionChoice } from './ui/ExtractionChoice';
import { describeGadgetFailure } from './ui/GadgetFailure';
import { itemSlotControl, readyItemControl } from './ui/ItemControls';
import { LobbyFlow } from './ui/LobbyFlow';
import { MatchFeed } from './ui/MatchFeed';
import { ResultManifest } from './ui/ResultManifest';
import { bindResultAccount } from './ui/ResultAccount';
import { describeMatchScore, matchResultHeading } from './ui/ResultScore';
import { RunNoticeArbiter, type RunNoticePriority } from './ui/RunNoticePriority';
import { RunLaunchScope } from './ui/RunLaunchScope';
import { RunPreparationView } from './ui/RunPreparationView';
import { RunBoardingPresentation } from './ui/RunBoardingPresentation';
import { RunRoster } from './ui/RunRoster';
import { runIdentityLabel } from './ui/RunIdentity';
import { SocialFlow } from './ui/SocialFlow';
import { StationChatFlow } from './ui/StationChatFlow';
import { describeSupply } from './ui/SupplyClock';
import { TextBindings } from './ui/TextBindings';
import { projectTraversalPrompt } from './ui/TraversalPrompt';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('Missing #app root');
const root = app;
const runtimeConfig = createRuntimeConfig(import.meta.env.VITE_API_ORIGIN, window.location.origin);
const moduleRecovery = new ModuleRecoveryGuard(() => sessionStorage, () => location.reload());

renderAppShell(root);
const textBindings = new TextBindings(root);

const landing = required<HTMLElement>('[data-landing]');
const gameScreen = required<HTMLElement>('[data-game-screen]');
const gameCanvas = required<HTMLCanvasElement>('[data-game-canvas]');
const rendererPreparation = new RunPreparationView({
  panel: required<HTMLElement>('[data-renderer-preparation]'),
  title: required<HTMLElement>('[data-preparation-title]'),
  detail: required<HTMLElement>('[data-preparation-detail]'),
});
const runLaunchScope = new RunLaunchScope();
const boardingPresentation = new RunBoardingPresentation({ landing, screen: gameScreen },
  () => lobbyFlow.hideForMatch());
const pauseLayer = required<HTMLElement>('[data-pause]');
const pauseEyebrow = required<HTMLElement>('[data-pause-eyebrow]');
const pauseHeading = required<HTMLElement>('[data-pause-heading]');
const pauseCopy = required<HTMLElement>('[data-pause-copy]');
const resumeButton = required<HTMLButtonElement>('[data-resume]');
const resultLayer = required<HTMLElement>('[data-result]');
const onboarding = required<HTMLElement>('[data-onboarding]');
const fallFlash = required<HTMLElement>('[data-fall-flash]');
const toast = required<HTMLElement>('[data-toast]');
const settings = required<HTMLDialogElement>('[data-settings]');
const volumeInput = required<HTMLInputElement>('[data-volume]');
const volumeOutput = required<HTMLOutputElement>('[data-volume-output]');
const sensitivityInput = required<HTMLInputElement>('[data-sensitivity]');
const sensitivityOutput = required<HTMLOutputElement>('[data-sensitivity-output]');
const graphicsInput = required<HTMLSelectElement>('[data-graphics]');
const matchFeed = new MatchFeed(root);
const combatFeedback = new CombatFeedback(root);
const runRoster = new RunRoster(root);
const resultManifest = new ResultManifest(root);
const runNotices = new RunNoticeArbiter();

interface GameRuntimeModule {
  Game: typeof Game;
}

let game: Game | null = null;
const gameRuntime = new CachedModuleImport<GameRuntimeModule>(
  new URL(gameRuntimeURL, location.href).href,
  url => import(/* @vite-ignore */ url),
);
let toastTimer = 0;
let onboardingTimer = 0;
let bankInterruptedUntil = 0;
let gadgetFailureTimer = 0;
let supplyPulseTimer = 0;
let volume = readStoredVolume();
let sensitivity = readStoredSensitivity();
let graphicsPreference = readStoredGraphicsPreference();
let matchTimeRemaining: number | null = null;
let multiplayerActive = false;
let activeRunMode: Lobby['mode'] | null = null;
let activePlayerID = '';
let currentLaunchState: Snapshot['launchState'];
let currentSupplyWave: number | null = null;
let realtimeConnectionState: ConnectionState = 'closed';
let currentItems: readonly NetworkItemState[] = [];
let currentGadgetState = { grappleCooldown: 0, repulsorCooldown: 0, magnetCooldown: 0, grappleActive: false };
volumeInput.value = String(Math.round(volume * 100));
volumeOutput.value = `${Math.round(volume * 100)}%`;
sensitivityInput.value = String(Math.round(sensitivity * 100));
sensitivityOutput.value = `${Math.round(sensitivity * 100)}%`;
graphicsInput.value = graphicsPreference;

const identity = new IdentityService(runtimeConfig);
const resultAccount = bindResultAccount(root, identity);
new AccountFlow(root, identity);
new SessionRecovery(root, identity);
new PasswordRotation(root, identity);
new SocialFlow(root, runtimeConfig, identity);
const stationChat = new StationChatFlow(root, runtimeConfig, identity, () => location.pathname === '/');
const lobbyFlow = new LobbyFlow(root, runtimeConfig, identity, {
  onLobbyEnter: () => stationChat.leave(),
  onLobbyLeave: () => stationChat.enter(),
  onMatchStart: (client, credentials, lobby) => { void startRun(client, credentials, lobby); },
  onMatchDeparture: () => runLaunchScope.cancel(),
  onMatchPreparationCancelled: clearRunPresentation,
  onSnapshot: (snapshot) => {
    if (snapshot.phase === 'active' || snapshot.phase === 'ending') boardingPresentation.depart();
    game?.applyNetworkSnapshot(snapshot);
  },
  onSnapshotDelivery: (diagnostics) => {
    gameCanvas.dataset.snapshotReceived = String(diagnostics.received);
    gameCanvas.dataset.snapshotApplied = String(diagnostics.applied);
    gameCanvas.dataset.snapshotCoalesced = String(diagnostics.coalesced);
    gameCanvas.dataset.snapshotMaximumBatch = String(diagnostics.maximumBatch);
    gameCanvas.dataset.snapshotDiscardedEvents = String(diagnostics.discardedEvents);
  },
  onConnectionState: (state) => {
    realtimeConnectionState = state;
    const status = required<HTMLElement>('[data-network-status]');
    status.dataset.state = state;
    status.textContent = state === 'connected' ? 'SERVER 20HZ' : state.toUpperCase();
    game?.setNetworkConnected(state === 'connected');
    updateNetworkInterruption(state);
  },
  onIdentityDeparture: () => { void returnToYard(); },
});

const trainingButton = required<HTMLButtonElement>('[data-training]');
const createLobbyButton = required<HTMLButtonElement>('[data-create-lobby]');
const quickplayButton = required<HTMLButtonElement>('[data-quickplay]');
for (const button of [trainingButton, createLobbyButton, quickplayButton]) {
  button.addEventListener('pointerenter', warmGameBundle, { once: true });
  button.addEventListener('focus', warmGameBundle, { once: true });
}
resumeButton.addEventListener('click', () => game?.resume());
required<HTMLButtonElement>('[data-abort]').addEventListener('click', () => { void returnToYard(); });
required<HTMLButtonElement>('[data-replay]').addEventListener('click', () => { void replayRun(); });
required<HTMLButtonElement>('[data-result-menu]').addEventListener('click', () => { void returnToYard(); });
root.querySelectorAll<HTMLButtonElement>('[data-settings-open]').forEach((button) => {
  button.addEventListener('click', () => settings.showModal());
});
gameCanvas.addEventListener('click', () => game?.resume());

async function replayRun(): Promise<void> {
  if (!multiplayerActive) {
    lobbyFlow.queueTraining();
    return;
  }
  const replayMode = activeRunMode;
  await returnToYard();
  if (replayMode === 'training') lobbyFlow.queueTraining();
  else lobbyFlow.queueQuickplay();
}
volumeInput.addEventListener('input', () => {
  volume = Number(volumeInput.value) / 100;
  volumeOutput.value = `${volumeInput.value}%`;
  game?.setVolume(volume);
  localStorage.setItem('scrapline:volume', String(volume));
});
sensitivityInput.addEventListener('input', () => {
  sensitivity = Number(sensitivityInput.value) / 100;
  sensitivityOutput.value = `${sensitivityInput.value}%`;
  game?.setLookSensitivity(sensitivity);
  localStorage.setItem('scrapline:sensitivity', String(sensitivity));
});
graphicsInput.addEventListener('change', () => {
  graphicsPreference = graphicsInput.value === 'performance' ? 'performance' : 'auto';
  game?.setGraphicsPreference(graphicsPreference);
  localStorage.setItem('scrapline:graphics', graphicsPreference);
});
// The static CSP-compatible watchdog owns the page only until the complete
// Station shell and its controllers are synchronously ready for interaction.
window.dispatchEvent(new Event('scrapline:shell-ready'));
required<HTMLButtonElement>('[data-cancel-preparation]').addEventListener('click', () => { void returnToYard(); });

async function startRun(realtime?: RealtimeClient, credentials?: Credentials, lobby?: Lobby): Promise<void> {
  resultAccount.clear();
  const rendererCapabilities = probeRendererCapabilities();
  if (!rendererCapabilities.supported) {
    showLaunchFailure(credentials, '3D RENDERER UNAVAILABLE // ENABLE WEBGL 2 OR HARDWARE ACCELERATION');
    return;
  }
  const attempt = runLaunchScope.begin();
  game?.dispose();
  game = null;
  multiplayerActive = Boolean(realtime && credentials);
  activeRunMode = lobby?.mode ?? null;
  stationChat.leave();
  boardingPresentation.begin(lobby?.phase === 'starting');
  pauseLayer.classList.remove('is-visible');
  resultLayer.classList.remove('is-visible');
  gameCanvas.dataset.initialFramePrepared = 'false';
  rendererPreparation.show('package');
  let GameRuntime: typeof Game;
  try {
    ({ Game: GameRuntime } = await loadGameRuntime());
  } catch (error) {
    if (attempt.aborted) return;
    console.error('Game runtime failed to load', error);
    if (retryRuntimeAfterReload()) return;
    await returnToYard();
    if (!runLaunchScope.active) showLaunchFailure(undefined, 'GAME PACKAGE FAILED TO LOAD // CHECK YOUR CONNECTION AND REFRESH');
    return;
  }
  if (attempt.aborted) return;
  moduleRecovery.clearAfterSuccess();
  if (!multiplayerActive) realtimeConnectionState = 'closed';
  activePlayerID = credentials?.playerId ?? '';
  currentLaunchState = undefined;
  currentSupplyWave = null;
  currentItems = [];
  bankInterruptedUntil = 0;
  matchTimeRemaining = null;
  currentGadgetState = { grappleCooldown: 0, repulsorCooldown: 0, magnetCooldown: 0, grappleActive: false };
  runRoster.setActive(multiplayerActive, activePlayerID);
  renderItemSlots();
  matchFeed.clear();
  combatFeedback.clear();
  clearRunNotice();
  clearGadgetFailures();
  updateNearbyPickup(null);
  updateSupply();
  resultManifest.reset();
  required<HTMLButtonElement>('[data-save-progress]').hidden = !credentials;
  const training = activeRunMode === 'training';
  const openingNotice = credentials
    ? training
      ? '<b>LIVE COMBAT DRILL.</b><span>Four Yardbots use the same weapons, grabs, Q/E/R items, drops, coupler, timer, and extraction rules as public runs. Your first action releases the field.</span>'
      : '<b>RACE. RAID. SURVIVE.</b><span>Reach the locomotive before LINE 00:00. Q/E/R use breakable item slots. Hold M1 at freight or a train lip to grab and climb; release to drop. Face a grab and hold RMB to brace. Every punch, swing, and shot uses RMB + M1.</span>'
    : '<b>STAY ON THE TRAIN.</b><span>Move with WASD. Mouse to look. Cross the consist and reach the orange marker.</span>';
  showRunNotice(openingNotice, 7000, undefined, 'action');
  const worldSeed = credentials ? seedFromText(credentials.lobbyCode) : Math.floor(Date.now() / 86_400_000);
  setText('[data-run-label]', runIdentityLabel(credentials, training));
  const networkStatus = required<HTMLElement>('[data-network-status]');
  networkStatus.textContent = credentials ? 'SERVER 20HZ' : 'LOCAL';
  networkStatus.dataset.state = credentials ? 'connected' : 'closed';
  setText('[data-live-players]', credentials ? '0' : '1');
  setText('[data-server-tick]', '0');
  setText('[data-server-ack]', '0');
  setText('[data-objective-label]', credentials ? 'REACH LOCOMOTIVE // TRAIN 1 // ×1.00' : 'REACH THE LOCOMOTIVE');
  setText('[data-time-kind]', credentials ? 'LINE' : 'RUN');
  setText('[data-loot-unit]', credentials ? 'CARRIED / SIGNALS' : 'RECOVERED');
  required<HTMLElement>('[data-action-control]').innerHTML = credentials ? '<b>X / T</b> CASH OUT / NEXT TRAIN' : '<b>AUTO</b> REBOARD';
  required<HTMLElement>('[data-roster-control]').hidden = !credentials;
  required<HTMLElement>('.controls-hint').classList.toggle('is-live', Boolean(credentials));
  required<HTMLElement>('[data-gadget-dock]').classList.toggle('is-visible', Boolean(credentials));
  required<HTMLElement>('[data-combat-status]').classList.toggle('is-visible', Boolean(credentials));
  required<HTMLElement>('[data-coupler-warning]').className = 'coupler-warning';
  required<HTMLElement>('[data-coupler-prompt]').className = 'interaction-prompt';
  required<HTMLElement>('[data-hazard-warning]').className = 'hazard-warning';
  required<HTMLElement>('[data-traversal-prompt]').className = 'traversal-prompt';
  required<HTMLElement>('[data-crosswind-overlay]').className = 'crosswind-overlay';
  required<HTMLElement>('[data-extraction-callout]').className = 'extraction-callout';
  required<HTMLElement>('[data-extraction-choice]').hidden = true;
  gameScreen.classList.remove('is-extraction-deciding');
  setText('[data-result-eyebrow]', 'LOCOMOTIVE REACHED');
  setText('[data-result-heading]', 'HAUL SECURED.');
  setText('[data-result-loot-label]', 'SALVAGE');
  setText('[data-result-items-label]', 'RECOVERED');
  setText('[data-result-time-label]', 'RUN TIME');
  required<HTMLElement>('[data-result-grid]').classList.remove('is-match');
  required<HTMLElement>('[data-result-eliminations-cell]').hidden = true;
  let startingGame: Game | undefined;
  try {
    rendererPreparation.show('scene');
    startingGame = new GameRuntime(gameCanvas, {
      onFrame: updateHUD,
      onPickup: (item) => showPickup(item.label, item.value),
      onFall: (falls) => showFall(falls),
      onPauseChange: (paused, reason) => {
        const captureRequired = paused && reason === 'capture-required';
        pauseEyebrow.textContent = captureRequired ? 'CONTROL LINK REQUIRED' : 'RUN PAUSED';
        pauseHeading.textContent = captureRequired ? 'TAKE THE CONTROLS.' : "THE TRAIN WON'T WAIT.";
        pauseCopy.textContent = captureRequired
          ? 'Click once to lock the mouse. Your runner stays still until control is connected.'
          : "Your runner is holding position. Resume when you're ready.";
        resumeButton.textContent = captureRequired ? 'TAKE CONTROL' : 'RESUME RUN';
        pauseLayer.classList.toggle('is-visible', paused);
      },
      onComplete: showResult,
      onNetworkFrame: (state) => {
        setText('[data-live-players]', String(state.connectedPlayers));
        setText('[data-server-tick]', String(state.tick));
        setText('[data-server-ack]', String(state.lastInput));
      },
      onLaunchState: updateLaunchState,
      onExtraction: updateExtraction,
      onSupply: updateSupply,
      onMatchEvent: showMatchEvent,
      onMatchResult: showMatchResult,
      onResults: (results, players) => resultManifest.update(results, players, credentials?.playerId ?? ''),
      onGadgets: updateGadgets,
      onGadgetFailure: showGadgetFailure,
      onItems: updateItems,
      onCoupler: updateCoupler,
      onHazard: updateHazard,
      onWeapon: updateWeapon,
      onRoster: (players, routeStartZ, routeFinishZ) => runRoster.update(players, routeStartZ, routeFinishZ),
      onNearbyPickup: updateNearbyPickup,
    }, realtime && credentials ? {
      localPlayerId: credentials.playerId,
      worldSeed,
      sendInput: (input) => realtime.input(input),
      sendPickup: (targetId) => realtime.pickup(targetId),
      sendExtract: () => realtime.extract(),
      sendAdvance: () => realtime.advance(),
      sendItem: (slot, seq) => realtime.item(slot, seq),
      flushBeforeRender: () => realtime.flushBeforeRender(),
      sendCoupler: () => realtime.coupler(),
      sendPrimary: () => realtime.primary(),
      sendWeaponSwap: (targetId) => realtime.swapWeapon(targetId),
      sendItemSwap: (targetId, slot) => realtime.swapItem(targetId, slot),
    } : undefined, { antialias: !rendererCapabilities.software });
    game = startingGame;
    startingGame.setVolume(volume);
    startingGame.setLookSensitivity(sensitivity);
    startingGame.setGraphicsPreference(graphicsPreference);
    startingGame.setNetworkConnected(!credentials || realtimeConnectionState === 'connected');
    const prepared = await startingGame.prepare();
    if (attempt.aborted || game !== startingGame || !prepared) return;
    rendererPreparation.hide();
    if (!boardingPresentation.activate()) return;
    startingGame.start();
  } catch (error) {
    if (attempt.aborted || (startingGame && game !== startingGame)) return;
    rendererPreparation.hide();
    const signalMissing = error instanceof InitialSceneTimeout;
    if (signalMissing) console.warn('Train signal did not arrive during loading');
    else console.error('Game renderer failed to start', error);
    await returnToYard();
    if (runLaunchScope.active) return;
    showLaunchFailure(undefined, signalMissing
      ? 'TRAIN SIGNAL TIMED OUT // CHECK YOUR CONNECTION AND BOARD AGAIN'
      : '3D RENDERER FAILED TO START // UPDATE YOUR BROWSER OR GRAPHICS DRIVER');
  }
}

function warmGameBundle(): void {
  void requestGameRuntime().catch(() => undefined);
}

async function loadGameRuntime(): Promise<GameRuntimeModule> {
  let lastError: unknown;
  for (const retryDelay of [0, 180, 520]) {
    if (retryDelay > 0) await new Promise((resolve) => window.setTimeout(resolve, retryDelay));
    try {
      return await requestGameRuntime();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

function requestGameRuntime(): Promise<GameRuntimeModule> {
  return gameRuntime.request();
}

function retryRuntimeAfterReload(): boolean {
  return moduleRecovery.retryAfterReload();
}

function updateHazard(status: HazardStatus, exposure: NetworkPlayer['crosswindExposure'] = 'exposed'): void {
  const warning = required<HTMLElement>('[data-hazard-warning]');
  const overlay = required<HTMLElement>('[data-crosswind-overlay]');
  const direction = status.direction > 0 ? 'right' : 'left';
  warning.className = `hazard-warning${status.state === 'quiet' ? '' : ` is-visible is-${status.state} is-${direction}`}`;
  overlay.className = `crosswind-overlay${status.state === 'quiet' ? '' : ` is-visible is-${status.state} is-${direction}`}`;
  const safe = exposure === 'sheltered' || exposure === 'protected' || exposure === 'inactive';
  warning.classList.toggle('is-sheltered', safe && status.state !== 'quiet');
  overlay.classList.toggle('is-sheltered', safe && status.state !== 'quiet');
  warning.dataset.exposure = exposure;
  setText('[data-crosswind-flow]', direction === 'right' ? '→   →   →   →   →' : '←   ←   ←   ←   ←');
  const side = status.direction > 0 ? 'RIGHT' : 'LEFT';
  if (status.state === 'warning') {
    setText('[data-hazard-title]', `WIND FRONT → ${side}`);
    setText('[data-hazard-detail]', `${status.activatesIn.toFixed(1)}S TO IMPACT // SEEK A LOW DECK`);
  } else if (status.state === 'active') {
    setText('[data-hazard-title]', `GUST FORCE → ${side}`);
    setText('[data-hazard-detail]', `${status.endsIn.toFixed(1)}S REMAIN // HOLD M1 NEAR A LIP TO CATCH`);
  }
  if (status.state !== 'quiet' && safe) {
    const remaining = status.state === 'warning'
      ? `${status.activatesIn.toFixed(1)}S TO IMPACT`
      : `${status.endsIn.toFixed(1)}S REMAIN`;
    setText('[data-hazard-title]', exposure === 'sheltered' ? 'LOW DECK · SHELTERED' : 'WIND PROTECTION ACTIVE');
    setText('[data-hazard-detail]', `${remaining} // ${exposure === 'sheltered' ? 'STAY LOW TO AVOID THE GUST' : 'TEMPORARY SAFETY · SEEK A LOW DECK'}`);
  }
  if (status.state !== 'quiet' && exposure === 'below') {
    setText('[data-hazard-title]', 'BELOW THE GUST · AIRBORNE');
    setText('[data-hazard-detail]', 'HOLD M1 NEAR A LIP TO CATCH');
  }
}

function updateGadgets(state: { grappleCooldown: number; repulsorCooldown: number; magnetCooldown: number; grappleActive: boolean }): void {
  currentGadgetState = state;
  renderItemSlots();
}

function updateItems(items: readonly NetworkItemState[]): void {
  currentItems = items;
  renderItemSlots();
}

function renderItemSlots(): void {
  for (let index = 0; index < 3; index += 1) {
    const item = currentItems[index];
    const id = item?.id;
    const durability = item?.durability ?? 0;
    const definition = id ? gadgetDefinitions[id] : null;
    const cooldown = id === 'grapple'
      ? currentGadgetState.grappleCooldown
      : id === 'repulsor' ? currentGadgetState.repulsorCooldown : id === 'magnet' ? currentGadgetState.magnetCooldown : 0;
    const slot = required<HTMLElement>(`[data-item-slot="${index}"]`);
    slot.dataset.gadget = id ?? 'empty';
    const ready = Boolean(definition && durability > 0 && cooldown <= 0.02);
    slot.classList.toggle('is-ready', ready);
    slot.classList.toggle('is-active', id === 'grapple' && currentGadgetState.grappleActive);
    required<HTMLElement>('[data-item-name]', slot).textContent = definition?.label ?? 'EMPTY SLOT';
    const status = required<HTMLElement>('[data-item-status]', slot);
    const charge = required<HTMLElement>('[data-item-charge]', slot);
    if (id) {
      status.dataset.gadgetStatus = id;
      charge.dataset.gadgetCharge = id;
    } else {
      delete status.dataset.gadgetStatus;
      delete charge.dataset.gadgetCharge;
    }
    status.textContent = !definition
      ? 'WALK OVER AN ITEM'
      : cooldown > 0.02 ? `${cooldown.toFixed(1)}S // ${durability} USES` : `READY // ${durability} USES`;
    charge.style.width = `${definition ? Math.max(0, 1-cooldown/definition.cooldown)*100 : 0}%`;
  }
}

function showGadgetFailure(failure: GadgetFailureNotice): void {
  const description = describeGadgetFailure(failure);
  const slot = required<HTMLElement>(`[data-item-slot="${failure.slot}"]`);
  window.clearTimeout(gadgetFailureTimer);
  root.querySelectorAll<HTMLElement>('[data-item-slot]').forEach((element) => element.classList.remove('is-denied'));
  slot.dataset.failure = failure.reason;
  void slot.offsetWidth;
  slot.classList.add('is-denied');
  gadgetFailureTimer = window.setTimeout(() => slot.classList.remove('is-denied'), 520);
  showRunNotice(`<b>${description.heading}</b><span>${description.copy}</span>`, 1900, undefined, 'action');
}

function clearGadgetFailures(): void {
  window.clearTimeout(gadgetFailureTimer);
  gadgetFailureTimer = 0;
  root.querySelectorAll<HTMLElement>('[data-item-slot]').forEach((slot) => {
    slot.classList.remove('is-denied');
    delete slot.dataset.failure;
  });
}

function updateWeapon(state: { health: number; maxHealth: number; eliminations: number; shield: number; weapon: NetworkWeaponState }): void {
  const healthRatio = Math.max(0, Math.min(1, state.health/Math.max(1, state.maxHealth)));
  setText('[data-health-label]', state.shield > 0 ? `${state.health} // SHIELD ${state.shield.toFixed(1)}` : String(state.health));
  required<HTMLElement>('[data-health-bar]').style.width = `${healthRatio*100}%`;
  required<HTMLElement>('[data-combat-status]').classList.toggle('is-hurt', healthRatio < 0.4);
  required<HTMLElement>('[data-combat-status]').classList.toggle('is-shielded', state.shield > 0);
  setText('[data-eliminations]', String(state.eliminations));
  const identified = state.weapon.id ? weaponDefinitions[state.weapon.id as keyof typeof weaponDefinitions] : null;
  const remaining = state.weapon.durability ?? state.weapon.ammo;
  const definition = remaining > 0 ? identified : null;
  const readout = required<HTMLElement>('[data-weapon-readout]');
  readout.classList.toggle('is-armed', Boolean(definition));
  required<HTMLElement>('[data-crosshair]').classList.toggle('is-armed', Boolean(definition));
  setText('[data-primary-control]', definition ? 'RMB+M1' : 'M1');
  setText('[data-weapon-name]', definition?.label ?? 'UNARMED');
  // The attack key already spells out RMB+M1. Keep the role concise so narrow
  // HUDs can show every equipped item without shrinking essential text.
  setText('[data-weapon-role]', definition ? definition.role : 'RMB BRACE // RMB+M1 PUNCH');
  const displayed = state.weapon.kind === 'melee' ? remaining : state.weapon.ammo;
  const maximum = state.weapon.kind === 'melee' ? state.weapon.maxDurability ?? 0 : state.weapon.maxAmmo;
  setText('[data-weapon-ammo]', definition ? String(displayed).padStart(2, '0') : '—');
  setText('[data-weapon-max]', definition ? `/${maximum} DUR` : '');
  const cooldown = definition ? definition.cooldown : 1;
  required<HTMLElement>('[data-weapon-cycle]').style.width = `${Math.max(0, 1-state.weapon.cooldown/cooldown)*100}%`;
}

function updateSupply(status?: SupplyStatus): void {
  const clock = required<HTMLElement>('[data-supply-clock]');
  const label = status ? describeSupply(status) : null;
  clock.hidden = !label;
  if (!status) {
    currentSupplyWave = null;
    clock.classList.remove('is-arrived');
    window.clearTimeout(supplyPulseTimer);
    return;
  }
  if (label) clock.textContent = label;
  if (currentSupplyWave !== null && status.wave > currentSupplyWave) {
    clock.classList.remove('is-arrived');
    // Restarting the same short animation makes consecutive authoritative
    // waves legible even if a background tab missed the prior transition.
    void clock.offsetWidth;
    clock.classList.add('is-arrived');
    window.clearTimeout(supplyPulseTimer);
    supplyPulseTimer = window.setTimeout(() => clock.classList.remove('is-arrived'), 1700);
  }
  currentSupplyWave = status.wave;
}

function updateNearbyPickup(notice: PickupAwarenessNotice | null): void {
  const scanner = required<HTMLElement>('[data-pickup-scanner]');
  scanner.classList.toggle('is-visible', Boolean(notice));
  scanner.setAttribute('aria-hidden', String(!notice));
  if (!notice) {
    delete scanner.dataset.kind;
    delete scanner.dataset.status;
    return;
  }
  scanner.dataset.kind = notice.kind;
  scanner.dataset.status = notice.status;
  setText('[data-pickup-category]', notice.category);
  setText('[data-pickup-name]', notice.label);
  setText('[data-pickup-detail]', notice.detail);
  setText('[data-pickup-distance]', `${Math.max(1, Math.ceil(notice.distance))}M`);
}

function updateNetworkInterruption(state: ConnectionState): void {
  const interruption = required<HTMLElement>('[data-network-interruption]');
  interruption.classList.toggle('is-visible', multiplayerActive && state !== 'connected' && state !== 'closed');
}

function updateCoupler(status: CouplerStatus, nearby: boolean): void {
  const prompt = required<HTMLElement>('[data-coupler-prompt]');
  prompt.classList.toggle('is-visible', status.state === 'ready' && nearby);
  required<HTMLElement>('[data-pickup-scanner]').classList.toggle('is-suppressed', status.state === 'ready' && nearby);
  const warning = required<HTMLElement>('[data-coupler-warning]');
  warning.className = `coupler-warning${status.state === 'ready' ? '' : ` is-visible is-${status.state}`}`;
  if (status.state === 'armed') {
    setText('[data-coupler-title]', 'REAR LINK ARMED');
    setText('[data-coupler-detail]', `SEPARATION IN ${status.activatesIn.toFixed(1)}S`);
  } else if (status.state === 'separated') {
    setText('[data-coupler-title]', 'REAR SECTION DRIFTING');
    setText('[data-coupler-detail]', `GAP ${status.offset.toFixed(1)}M // GRAPPLE FORWARD`);
  } else if (status.state === 'lost') {
    setText('[data-coupler-title]', 'REAR SECTION LOST');
    setText('[data-coupler-detail]', 'EXTRACTION REMAINS AT LOCOMOTIVE');
  }
}

async function returnToYard(): Promise<void> {
  const wasMultiplayer = multiplayerActive;
  clearRunPresentation();
  const departure = wasMultiplayer ? lobbyFlow.leave() : Promise.resolve();
  if (!wasMultiplayer) stationChat.enter();
  await departure;
}

function clearRunPresentation(): void {
  runLaunchScope.cancel();
  rendererPreparation.hide();
  resultAccount.clear();
  // A failed package returned us here: retain its bounded reload guard until
  // an actual successful import clears it, rather than reloading on each retry.
  game?.dispose();
  game = null;
  boardingPresentation.reset();
  gameScreen.classList.remove('is-extraction-deciding');
  resultLayer.classList.remove('is-visible');
  pauseLayer.classList.remove('is-visible');
  multiplayerActive = false;
  activeRunMode = null;
  updateNetworkInterruption('closed');
  activePlayerID = '';
  currentLaunchState = undefined;
  bankInterruptedUntil = 0;
  runRoster.setActive(false);
  matchFeed.clear();
  combatFeedback.clear();
  clearRunNotice();
  clearGadgetFailures();
  updateNearbyPickup(null);
}

function updateHUD(state: GameFrameState): void {
  if (state.grabRelation?.role !== 'held' && runNotices.release('held')) clearRunNotice();
  setText('[data-loot]', state.lootValue.toString().padStart(3, '0'));
  setText('[data-collected]', String(state.collected));
  setText('[data-total]', String(state.totalPickups));
  setText('[data-speed]', state.speed.toFixed(1).padStart(4, '0'));
  setText('[data-time]', multiplayerActive
    ? matchTimeRemaining === null ? '--:--' : formatCountdown(matchTimeRemaining)
    : formatTime(state.elapsedSeconds));
  const progress = Math.round(state.progress * 100);
  setText('[data-progress-label]', `${progress}%`);
  required<HTMLElement>('[data-progress]').style.width = `${progress}%`;
  const traversal = required<HTMLElement>('[data-traversal-prompt]');
  const prompt = projectTraversalPrompt(state.climbing, state.grabRelation,
    readyItemControl(currentItems, 'repulsor', currentGadgetState.repulsorCooldown));
  traversal.className = `traversal-prompt${prompt.visible ? ` is-visible is-${prompt.mode}` : ''}`;
  traversal.setAttribute('aria-hidden', String(!prompt.visible));
  setText('[data-traversal-eyebrow]', prompt.eyebrow);
  setText('[data-traversal-title]', prompt.title);
  setText('[data-traversal-detail]', prompt.detail);
  required<HTMLElement>('[data-pickup-scanner]').classList.toggle('is-suppressed', prompt.visible);
  gameCanvas.dataset.traversalState = state.climbing ? 'climbing' : 'free';
  gameCanvas.dataset.grabState = state.grabRelation?.role ?? 'free';
}

function updateExtraction(status: ExtractionStatus, insideZone: boolean, progress: number, carriedValue: number): void {
  if (!multiplayerActive) return;
  matchTimeRemaining = Math.max(0, status.matchEndsIn);
  const callout = required<HTMLElement>('[data-extraction-callout]');
  const normalizedProgress = Math.max(0, Math.min(1, progress));
  required<HTMLElement>('[data-extraction-progress]').style.width = `${normalizedProgress*100}%`;
  callout.className = 'extraction-callout';
  const choice = required<HTMLElement>('[data-extraction-choice]');
  choice.hidden = !insideZone || normalizedProgress > 0;
  gameScreen.classList.toggle('is-extraction-deciding', !choice.hidden);
  if (!choice.hidden) {
    const projection = projectExtractionChoice(status, carriedValue);
    const riskChoice = required<HTMLElement>('[data-risk-choice]');
    callout.classList.add('is-deciding');
    // A locked bank AND locked switch is a holding pattern, not a decision.
    // Keep both clocks/controls readable in a smaller combat-safe notice.
    callout.classList.toggle('is-waiting', !projection.bankAvailable && !projection.canAdvance);
    choice.classList.toggle('is-locked', !projection.bankAvailable);
    riskChoice.classList.toggle('is-locked', !projection.canAdvance);
    setText('[data-bank-choice]', projection.bankAvailable ? `BANK ₵${projection.bankValue}` : 'BANK LINK LOCKED');
    setText('[data-bank-choice-detail]', projection.bankAvailable
      ? `SECURE NOW // ×${projection.currentMultiplier.toFixed(2)}`
      : `OPENS IN ${formatCountdown(status.opensIn)} // ₵${projection.bankValue}`);
    setText('[data-risk-choice-key]', projection.canAdvance ? 'T' : projection.hasNextTrain ? '…' : '—');
    setText('[data-risk-choice-label]', projection.canAdvance
      ? 'ONE MORE TRAIN'
      : projection.hasNextTrain ? `NEXT TRAIN IN ${formatCountdown(projection.advanceOpensIn)}` : 'END OF THE LINE');
    setText('[data-risk-choice-detail]', projection.canAdvance
      ? `CURRENT HAUL ₵${projection.riskValue} // ×${projection.nextMultiplier.toFixed(2)}`
      : projection.hasNextTrain
        ? `SWITCH LOCKED // NEXT RISK ×${projection.nextMultiplier.toFixed(2)}`
        : 'NO SIXTH TRAIN // HOLD THE CAB');
  }
  const maximumTrain = status.maximumTrain || maximumTrainCount;
  const finalTrain = status.stopNumber >= maximumTrain;
  const objective = extractionObjective(status, insideZone, normalizedProgress);
  setText('[data-objective-label]', `${objective} // TRAIN ${status.stopNumber}/${maximumTrain} // ${status.trainLabel ?? trainStageForNumber(status.stopNumber).label} // ×${status.multiplier.toFixed(2)}`);
  const open = status.state === 'open';
  callout.classList.add('is-visible', open ? 'is-open' : 'is-warning');
  if (performance.now() < bankInterruptedUntil) callout.classList.add('is-interrupted');
  if (insideZone) callout.classList.add('is-inside');
  if (!open) {
    const unlockedDuration = Math.max(0, status.matchEndsIn-status.opensIn);
    setText('[data-extraction-title]', insideZone ? 'CAB REACHED // EXTRACTION LOCKED' : 'EXTRACTION OPENS IN');
    setText('[data-extraction-time]', formatCountdown(status.opensIn));
    setText('[data-extraction-copy]', insideZone
      ? finalTrain
        ? `FINAL TRAIN // BANK OPENS IN ${formatCountdown(status.opensIn)}`
        : status.nextTrainState === 'approaching'
          ? `NEXT TRAIN COUPLES IN ${formatCountdown(status.nextTrainOpensIn ?? 0)} // HOLD THE CAB`
          : `HOLD FOR THE FINAL ${formatCountdown(unlockedDuration)} // OR T NEXT TRAIN ×${nextMultiplier(status.stopNumber).toFixed(2)}`
      : !finalTrain && status.nextTrainState === 'ready'
        ? `TRAIN ${status.stopNumber+1} COUPLED // REACH THE CAB TO SWITCH`
        : `FIGHT FORWARD NOW // BANKING UNLOCKS FOR FINAL ${formatCountdown(unlockedDuration)}`);
    return;
  }
  if (insideZone && normalizedProgress > 0) {
    const bankDuration = status.bankDuration ?? 1.6;
    callout.classList.add('is-banking');
    setText('[data-extraction-title]', 'BANKING HAUL // HOLD X');
    setText('[data-extraction-time]', `${Math.max(0, bankDuration*(1-normalizedProgress)).toFixed(1)}S`);
    setText('[data-extraction-copy]', 'STAY GROUNDED AND STILL // MOVEMENT OR IMPACT RESETS THE LINK');
    return;
  }
  setText('[data-extraction-title]', insideZone ? 'LOCOMOTIVE REACHED // DECIDE' : 'LINE CLOSES IN');
  setText('[data-extraction-time]', formatCountdown(status.closesIn));
  setText('[data-extraction-copy]', insideZone
    ? finalTrain
      ? `HOLD X TO BANK ×${status.multiplier.toFixed(2)} // FINAL TRAIN`
      : `HOLD X TO BANK ×${status.multiplier.toFixed(2)} // T NEXT TRAIN ×${nextMultiplier(status.stopNumber).toFixed(2)}`
    : 'REACH THE LOCOMOTIVE BEFORE THE LINE CLOSES');
}

function updateLaunchState(state: Snapshot['launchState']): void {
  if (!multiplayerActive || !state) return;
  if (state === currentLaunchState) {
    // A hazard, pickup, or combat notice may temporarily borrow this surface.
    // Once its timer hides, the next authoritative held snapshot must restore
    // the launch instruction; otherwise a long-loading/idle runner sees a
    // frozen field with no explanation of how to release it.
    if (state === 'held' && !onboarding.classList.contains('is-visible')) {
      // Presentation can be cleared independently by a transient surface
      // lifecycle. Release its stale lease before rebuilding the persistent
      // authoritative launch instruction.
      clearRunNotice();
      renderHeldLaunchNotice();
    }
    return;
  }
  const previous = currentLaunchState;
  currentLaunchState = state;
  clearRunNotice();

  if (state === 'held') {
    renderHeldLaunchNotice();
    return;
  }
  if (previous === 'held') {
    showRunNotice('<b>FIELD RELEASED // RUN THE LINE.</b><span>The pack is moving. Fight toward the locomotive, build your haul, and survive until banking opens.</span>', 3200, 'success');
  }
}

function renderHeldLaunchNotice(): void {
  onboarding.innerHTML = activeRunMode === 'training'
    ? '<b>DRILL HELD // YOU SET THE PACE.</b><span>Four Yardbots are frozen on their marks. Read Q/E/R, find the nearest weapon, then move or attack to release the field.</span>'
    : '<b>FIELD HELD // YOU SET THE PACE.</b><span>Your first move or action releases every runner together. Read Q/E/R, pick your route, then go.</span>';
  window.clearTimeout(onboardingTimer);
  runNotices.hold('critical');
  onboarding.dataset.priority = 'critical';
  onboarding.classList.remove('is-danger', 'is-success');
  onboarding.classList.add('is-visible', 'is-launch-held');
}

function showRunNotice(
  content: string,
  duration: number,
  tone?: 'danger' | 'success',
  priority: RunNoticePriority = tone === 'danger' ? 'danger' : 'action',
  context?: string,
): boolean {
  if (!runNotices.request(priority, duration, performance.now(), context)) return false;
  onboarding.innerHTML = content;
  onboarding.dataset.priority = priority;
  onboarding.classList.remove('is-danger', 'is-success', 'is-launch-held');
  onboarding.classList.add('is-visible');
  if (tone) onboarding.classList.add(`is-${tone}`);
  window.clearTimeout(onboardingTimer);
  onboardingTimer = window.setTimeout(() => {
    onboarding.classList.remove('is-visible', 'is-danger', 'is-success');
    delete onboarding.dataset.priority;
    runNotices.clear();
  }, duration);
  return true;
}

function clearRunNotice(): void {
  window.clearTimeout(onboardingTimer);
  onboardingTimer = 0;
  runNotices.clear();
  delete onboarding.dataset.priority;
  onboarding.classList.remove('is-visible', 'is-danger', 'is-success', 'is-launch-held');
}

function showMatchEvent(event: MatchEvent, players: readonly NetworkPlayer[]): void {
  if (event.type === 'weapon_fired' && event.sourceId === activePlayerID && event.outcome === 'blocked') {
    combatFeedback.blocked();
  }
  if ((event.type === 'weapon_hit' || event.type === 'player_hit') && event.sourceId === activePlayerID) {
    combatFeedback.confirmed();
  }
  if ((event.type === 'weapon_hit' || event.type === 'player_hit') && event.targetId === activePlayerID) {
    combatFeedback.incoming(players, activePlayerID, event.sourceId, event.position);
  }
  matchFeed.push(event, players, activePlayerID);
  if (event.type === 'loot_picked' && event.playerId === activePlayerID) {
    showPickup(event.label ?? 'Salvage secured', event.value ?? 0);
  } else if (event.type === 'loot_dropped' && event.playerId === activePlayerID) {
    showPickup('HAUL DROPPED — RIVALS GET FIRST CLAIM', -(event.value ?? 0));
  } else if (event.type === 'bank_interrupted' && event.targetId === activePlayerID) {
    bankInterruptedUntil = performance.now()+1_200;
    showRunNotice('<b>BANK LINK BROKEN.</b><span>An impact interrupted the transfer. Regain the cab, hold still, and hold X again.</span>', 2600, 'danger', 'critical');
  } else if (event.type === 'bank_interrupted' && event.sourceId === activePlayerID) {
    showRunNotice('<b>CASHOUT DENIED.</b><span>Their haul is still live. Keep them away from the bank link.</span>', 2200, 'success');
  } else if (event.type === 'player_hit' && (event.targetId === activePlayerID || event.sourceId === activePlayerID)) {
    const hitByOpponent = event.targetId === activePlayerID;
    const impact = event.label === 'punch' ? 'PUNCH' : 'REPULSOR';
    showRunNotice(hitByOpponent
      ? `<b>${impact} IMPACT.</b><span>Counter-steer, grapple, or prepare to lose your haul.</span>`
      : `<b>${impact} CONNECTED.</b><span>They are moving. Follow up before they recover.</span>`, 2200);
  } else if (event.type === 'weapon_picked' && event.playerId === activePlayerID) {
    const definition = weaponDefinitions[event.label as keyof typeof weaponDefinitions] ?? weaponDefinitions.rivet_caster;
    showRunNotice(`<b>${definition.label} ${event.outcome === 'swapped' ? 'SWAPPED' : 'ACQUIRED'}.</b><span>${definition.role}. Hold RMB, then press M1 to attack. M1 alone remains grab/climb. Every hit wears it down.</span>`, 3200);
  } else if (event.type === 'weapon_hit' && (event.targetId === activePlayerID || event.sourceId === activePlayerID)) {
    const hitByOpponent = event.targetId === activePlayerID;
    showRunNotice(hitByOpponent
      ? `<b>SUIT HIT // −${event.value ?? 0}.</b><span>Break line of sight, Grapple away, or return fire.</span>`
      : `<b>WEAPON CONNECTED // ${event.value ?? 0}.</b><span>Push the advantage before they recover.</span>`, 1700);
  } else if (event.type === 'player_knocked_out' && event.targetId === activePlayerID) {
    showRunNotice('<b>SUIT BREACHED // GEAR DROPPED.</b><span>Reboarding at the rear. Your loot, weapon, and Q/E/R items remain on the nearest safe deck.</span>', 3200, 'danger', 'critical');
  } else if (event.type === 'coupler_separated') {
    showRunNotice('<b>THE REAR CAR BROKE LOOSE.</b><span>Jump or Grapple toward the security car before the gap becomes fatal.</span>', 3600, 'danger');
  } else if (event.type === 'rear_cargo_staged') {
    const count = Math.max(1, event.value ?? 1);
    showRunNotice(`<b>REAR SALVAGE LANDED AHEAD.</b><span>${count} loose ${count === 1 ? 'signal is' : 'signals are'} on the forward flatbed. Cross the gap to contest them.</span>`, 3000);
  } else if (event.type === 'train_advanced' && event.playerId === activePlayerID) {
    const stage = trainStageForNumber(event.value ?? 2);
    showRunNotice(`<b>TRAIN ${stage.number}/${maximumTrainCount} // ${stage.label}.</b><span>${stage.subtitle}. Your haul stayed aboard at ${event.label ?? 'a higher multiplier'}. Reach the next locomotive before the line closes.</span>`, 4200, 'success');
  } else if (event.type === 'gadget_fired' && event.label === 'magnet' && event.sourceId === activePlayerID) {
    showRunNotice('<b>MAGNET LOCKED.</b><span>Keep the loot in range. Rivals can still intercept it before it reaches you.</span>', 2200, 'success');
  } else if (event.type === 'player_grabbed' && event.targetId === activePlayerID) {
    // Coalesced events may include a grab that has already ended or changed
    // captor. The final authoritative relation owns present-tense instructions.
    const holderID = players.find((player) => player.id === activePlayerID)?.grabbedById;
    if (holderID && holderID === event.sourceId) {
      showRunNotice('<b>YOU HAVE BEEN GRABBED.</b><span>Face the holder and use RMB + M1. A successful hit breaks their grip before they can throw you.</span>', 2600, 'danger', 'danger', 'held');
    }
  } else if (event.type === 'grab_broken' && event.sourceId === activePlayerID) {
    showRunNotice('<b>GRIP BROKEN.</b><span>Your counter connected. Move before they recover and grab again.</span>', 1800, 'success', 'danger');
  } else if (event.type === 'grab_broken' && event.targetId === activePlayerID) {
    showRunNotice('<b>RIVAL BROKE FREE.</b><span>Their counter connected. Brace or reposition before trying again.</span>', 1800, 'danger');
  } else if (event.type === 'unarmed_action' && event.label === 'grab_blocked' && event.sourceId === activePlayerID) {
    showRunNotice('<b>GRAB BRACED.</b><span>Flank them, catch them airborne, or force their guard open with a weapon or tool.</span>', 1900, 'danger');
  } else if (event.type === 'unarmed_action' && event.label === 'grab_blocked' && event.targetId === activePlayerID) {
    showRunNotice('<b>GRAB BLOCKED.</b><span>You faced the rush and held RMB. Counterattack before they circle behind you.</span>', 1900, 'success');
  } else if (event.type === 'takedown_bonus' && event.sourceId === activePlayerID) {
    showPickup('TAKEDOWN BOUNTY', event.value ?? 0);
  } else if ((event.type === 'weapon_broke' || event.type === 'item_broke') && event.playerId === activePlayerID) {
    showRunNotice(`<b>${event.type === 'weapon_broke' ? 'WEAPON' : 'ITEM'} BROKE.</b><span>Fresh gear drops across the train every 30 seconds.</span>`, 2400);
  } else if (event.type === 'hazard_active') {
    const side = (event.value ?? 1) > 0 ? 'right' : 'left';
    showRunNotice(`<b>CROSSWIND HITTING ${side.toUpperCase()}.</b><span>Counter-steer or get down into an open car until the gust passes.</span>`, 3200, 'danger');
  }
  if (event.type === 'gadget_fired' && event.sourceId === activePlayerID) {
    const slot = currentItems.findIndex((item) => item.id === event.label);
    if (slot >= 0) delete required<HTMLElement>(`[data-item-slot="${slot}"]`).dataset.failure;
  } else if (event.type === 'item_picked' && event.playerId === activePlayerID) {
    // Go omits zero-valued event fields; an absent value here means slot Q.
    const slot = event.value ?? 0;
    const slotKey = itemSlotControl(slot);
    if (!slotKey) return;
    delete required<HTMLElement>(`[data-item-slot="${slot}"]`).dataset.failure;
    if (event.outcome === 'swapped') {
      showRunNotice(`<b>${slotKey} TOOL REPLACED.</b><span>Your previous tool stays here with its remaining uses. Rivals can claim it.</span>`, 2200, 'success', 'action');
    }
  }
}

function showMatchResult(result: MatchResult): void {
  pauseLayer.classList.remove('is-visible');
  gameScreen.classList.remove('is-extraction-deciding');
  const extracted = result.outcome === 'extracted';
  setText('[data-result-eyebrow]', extracted ? `EXTRACTED // TRAIN ${result.extractionStop ?? 1}` : 'LINE CLOSED // HAUL FORFEITED');
  setText('[data-result-heading]', matchResultHeading(result));
  setText('[data-result-loot-label]', extracted ? 'BANKED VALUE' : 'LOST VALUE');
  setText('[data-result-loot]', `₵${extracted ? result.bankedValue : result.lostValue}`);
  setText('[data-result-items-label]', 'SCORE');
  setText('[data-result-items]', String(result.score));
  setText('[data-result-time-label]', 'MULTIPLIER');
  setText('[data-result-time]', `×${result.multiplier.toFixed(2)}`);
  setText('[data-result-falls]', String(result.falls));
  setText('[data-result-eliminations]', String(result.eliminations ?? 0));
  required<HTMLElement>('[data-result-grid]').classList.add('is-match');
  required<HTMLElement>('[data-result-eliminations-cell]').hidden = false;
  setText('[data-result-copy]', describeMatchScore(result));
  required<HTMLButtonElement>('[data-replay]').textContent = 'BOARD ANOTHER TRAIN';
  const save = required<HTMLButtonElement>('[data-save-progress]');
  save.hidden = false;
  resultAccount.show(true, result.receiptId);
  resultLayer.classList.add('is-visible');
}

function showPickup(label: string, value: number): void {
  setText('[data-toast-label]', label.toUpperCase());
  setText('[data-toast-value]', `${value >= 0 ? '+' : '−'}₵${Math.abs(value)}`);
  toast.classList.remove('is-visible');
  window.requestAnimationFrame(() => toast.classList.add('is-visible'));
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
}

function showFall(falls: number): void {
  fallFlash.classList.remove('is-visible');
  window.requestAnimationFrame(() => fallFlash.classList.add('is-visible'));
  window.setTimeout(() => fallFlash.classList.remove('is-visible'), 900);
  showRunNotice(`<b>YOU MISSED THE TRAIN.</b><span>Rivals get a 3s head start. Step clear, then return to recover gear. Falls this run: ${falls}</span>`, 2800, 'danger', 'critical');
}

function showResult(result: GameResult): void {
  pauseLayer.classList.remove('is-visible');
  required<HTMLElement>('[data-result-grid]').classList.remove('is-match');
  required<HTMLElement>('[data-result-eliminations-cell]').hidden = true;
  setText('[data-result-loot]', `₵${result.lootValue}`);
  setText('[data-result-items]', `${result.collected}/${result.totalPickups}`);
  setText('[data-result-time]', formatTime(result.elapsedSeconds));
  setText('[data-result-falls]', String(result.falls));
  const missed = result.totalPickups - result.collected;
  setText('[data-result-copy]', missed === 0
    ? 'Every signal found. That is a dangerously good reason to stay one more stop.'
    : `You left ${missed} signal${missed === 1 ? '' : 's'} behind. Run it cleaner?`);
  required<HTMLButtonElement>('[data-replay]').textContent = 'RUN IT AGAIN';
  required<HTMLButtonElement>('[data-save-progress]').hidden = false;
  resultAccount.show(false);
  resultLayer.classList.add('is-visible');
}

function setText(selector: string, value: string): void {
  textBindings.set(selector, value);
}

function required<T extends Element>(selector: string, parent: ParentNode = root): T {
  const element = parent.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}

function formatTime(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

function formatCountdown(seconds: number): string {
  const whole = Math.max(0, Math.ceil(seconds));
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

function readStoredVolume(): number {
  const raw = localStorage.getItem('scrapline:volume');
  if (raw === null) return 0.65;
  const stored = Number(raw);
  return Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : 0.65;
}

function readStoredSensitivity(): number {
  const raw = localStorage.getItem('scrapline:sensitivity');
  if (raw === null) return 1;
  const stored = Number(raw);
  return Number.isFinite(stored) && stored >= 0.4 && stored <= 1.8 ? stored : 1;
}

function readStoredGraphicsPreference(): GraphicsPreference {
  return localStorage.getItem('scrapline:graphics') === 'performance' ? 'performance' : 'auto';
}

function showLaunchFailure(credentials: Credentials | undefined, copy: string): void {
  const message = credentials
    ? required<HTMLElement>('[data-lobby-error]')
    : required<HTMLElement>('[data-join-context]');
  message.textContent = copy;
  message.dataset.danger = 'true';
  message.hidden = false;
  message.classList.add('is-visible');
}
